'use client';

import React, { useCallback } from 'react';
import ReactGoogleAutocomplete from 'react-google-autocomplete';
import { cn } from '@/lib/utils/cn';

export interface AddressDataV2 {
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  fullAddress?: string;
}

export interface AddressInputV2Props {
  label?: React.ReactNode;
  error?: string;
  className?: string;
  inputClassName?: string;
  errorClassName?: string;
  placeholder?: string;
  value?: string; // fullAddress string for display
  onChange?: (value: string) => void; // updates only the fullAddress string
  onAddressSelect?: (address: AddressDataV2) => void; // emits structured selection
  countries?: string[]; // ISO alpha-2 codes
  required?: boolean;
  disabled?: boolean;
}

const AddressInputV2: React.FC<AddressInputV2Props> = ({
  label,
  error,
  className,
  inputClassName,
  errorClassName,
  placeholder = 'Enter address',
  value = '',
  onChange,
  onAddressSelect,
  countries = ['us'],
  required = false,
  disabled = false,
}) => {
  const parseAddress = useCallback((components: any[] = [], formattedAddress: string = ''): AddressDataV2 => {
    const data: AddressDataV2 = {
      address: '',
      city: '',
      state: '',
      zip: '',
      country: '',
      fullAddress: formattedAddress,
    };

    const getComponent = (types: string[]) => components.find((c: any) => types.some(t => c.types?.includes(t)));

    const streetNumber = getComponent(['street_number']);
    const route = getComponent(['route']);
    if (streetNumber || route) {
      data.address = [streetNumber?.long_name, route?.long_name].filter(Boolean).join(' ');
    }
    const city = getComponent(['locality', 'administrative_area_level_2', 'sublocality', 'sublocality_level_1']);
    if (city) data.city = city.long_name;
    const state = getComponent(['administrative_area_level_1', 'administrative_area_level_2']);
    if (state) data.state = state.long_name;
    const zip = getComponent(['postal_code', 'postal_code_prefix']);
    if (zip) data.zip = zip.long_name;
    const country = getComponent(['country']);
    if (country) data.country = country.long_name;

    if (!data.address && formattedAddress) data.address = formattedAddress;
    return data;
  }, []);

  return (
    <div className={cn(className)}>
      {label ? (
        <label className="form-label">
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
      ) : null}

      <ReactGoogleAutocomplete
        apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY || ''}
        placeholder={placeholder}
        className={cn('form-control form-control-lg', inputClassName)}
        defaultValue={value}
        onChange={(e: any) => onChange?.(e?.target?.value ?? '')}
        onPlaceSelected={(place: any) => {
          if (!place) return;
          const components = place.address_components || [];
          const formatted = place.formatted_address || place.name || '';
          const parsed = parseAddress(components, formatted);
          onAddressSelect?.(parsed);
        }}
        options={{
          types: ['address'],
          componentRestrictions: countries && countries.length ? { country: countries as any } : undefined,
        }}
        fields={['address_components', 'formatted_address', 'name'] as any}
        disabled={disabled}
        {...(required ? { required: true } : {})}
      />

      {error && <div className={cn('invalid-feedback d-block', errorClassName)}>{error}</div>}
    </div>
  );
};

export default AddressInputV2;


